import { useCallback, useEffect, useState } from "react";
import { api, apiError, inr } from "@/lib/api";
import { toast } from "sonner";
import { Download, Upload, FileDown, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const RESULT = {
  matched: ["Matched", "text-emerald-400"], value_mismatch: ["Value mismatch", "text-amber-400"],
  not_in_books: ["Not in books", "text-rose-400"], cancelled: ["Cancelled", "text-muted-foreground"],
  cancelled_in_books: ["Cancelled but invoice booked", "text-rose-400"],
};
const btn = "flex items-center gap-1.5 rounded-md px-3 py-2 text-xs font-medium transition-colors disabled:opacity-50";

async function download(url, fallback) {
  try {
    const res = await api.get(url, { responseType: "blob" });
    const m = (res.headers["content-disposition"] || "").match(/filename\*=UTF-8''([^;]+)|filename=([^;]+)/);
    const a = document.createElement("a");
    a.href = URL.createObjectURL(res.data);
    a.download = m ? decodeURIComponent(m[1] || m[2]) : fallback;
    a.click(); URL.revokeObjectURL(a.href);
    toast.success("Downloaded");
  } catch (e) { toast.error(apiError(e)); }
}

function Counts({ rep }) {
  const items = [["matched", "Matched"], ["value_mismatch", "Value mismatch"], ["not_in_books", "Not in books"], ["cancelled_in_books", "Cancelled but booked"], ["missing_ewb", "Invoices ≥ ₹50,000 without EWB"]];
  return (
    <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="eway-counts">
      {items.map(([k, l]) => (
        <div key={k} className="rounded-lg border border-border bg-card p-3">
          <div className="small-label">{l}</div>
          <div className="font-mono text-lg mt-1" data-testid={`eway-count-${k}`}>{rep.counts[k]}</div>
        </div>
      ))}
      <div className="rounded-lg border border-border bg-card p-3"><div className="small-label">EWB value</div><div className="font-mono text-lg mt-1">{inr(rep.ewb_value)}</div></div>
    </div>
  );
}

function EwbTable({ rows }) {
  return (
    <div className="rounded-lg border border-border bg-card overflow-x-auto">
      <table className="w-full text-[11px] font-mono" data-testid="eway-table">
        <thead className="text-muted-foreground"><tr className="[&>th]:px-2 [&>th]:py-2 [&>th]:text-left border-b border-border">
          <th>EWB No</th><th>EWB Date</th><th>Doc No</th><th>Doc Date</th><th>Party GSTIN</th><th>Route</th>
          <th className="text-right">EWB Taxable</th><th className="text-right">Books Taxable</th><th className="text-right">Diff</th><th>Result</th>
        </tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={`${r.ewb_no}-${r.doc_no}`} className="border-b border-border/40 [&>td]:px-2 [&>td]:py-1.5" data-testid={`eway-row-${r.ewb_no}`}>
              <td>{r.ewb_no}</td><td className="whitespace-nowrap">{r.ewb_date}</td><td className="whitespace-nowrap">{r.doc_no}</td><td className="whitespace-nowrap">{r.doc_date}</td>
              <td>{r.party_gstin || "—"}</td><td className="whitespace-nowrap">{[r.from_state, r.to_state].filter(Boolean).join(" → ") || "—"}</td>
              <td className="text-right">{inr(r.taxable_value)}</td><td className="text-right">{r.book_taxable == null ? "—" : inr(r.book_taxable)}</td>
              <td className="text-right">{r.diff == null ? "—" : inr(r.diff)}</td>
              <td className={`whitespace-nowrap ${RESULT[r.result][1]}`}>{RESULT[r.result][0]}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={10} className="px-2 py-4 text-center text-muted-foreground font-sans">No e-way bills uploaded for this month.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function MissingTable({ rows }) {
  if (!rows.length) return null;
  return (
    <div className="rounded-lg border border-amber-500/30 bg-card overflow-x-auto" data-testid="eway-missing-table">
      <div className="px-4 py-2.5 border-b border-border text-sm font-head font-semibold">Invoices of goods ≥ ₹50,000 with no e-way bill</div>
      <table className="w-full text-[11px] font-mono">
        <thead className="text-muted-foreground"><tr className="[&>th]:px-2 [&>th]:py-2 [&>th]:text-left"><th>Invoice No</th><th>Date</th><th>Party</th><th>HSN</th><th className="text-right">Total value</th></tr></thead>
        <tbody>
          {rows.map((m) => (
            <tr key={m.invoice_no} className="border-t border-border/40 [&>td]:px-2 [&>td]:py-1.5">
              <td>{m.invoice_no}</td><td>{m.invoice_date}</td><td className="font-sans">{m.counterparty_name || m.counterparty_gstin || "—"}</td><td>{m.hsn}</td><td className="text-right">{inr(m.total_value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function EwayPanel({ clientId, period }) {
  const { user } = useAuth();
  const canEdit = user?.role !== "reader";
  const [direction, setDirection] = useState("outward");
  const [rep, setRep] = useState(null);
  const [busy, setBusy] = useState(false);
  const qs = `direction=${direction}&period=${period}`;
  const load = useCallback(() => {
    api.get(`/clients/${clientId}/eway/report?${qs}`).then((r) => setRep(r.data)).catch((e) => toast.error(apiError(e)));
  }, [clientId, qs]);
  useEffect(() => { load(); }, [load]);
  const upload = async (f) => {
    setBusy(true);
    const fd = new FormData(); fd.append("file", f);
    try { const { data } = await api.post(`/clients/${clientId}/eway/upload?${qs}`, fd); setRep(data); toast.success(`${data.rows.length} e-way bill(s) uploaded`); } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  const clear = async () => {
    if (!window.confirm("Remove the uploaded e-way bills for this month?")) return;
    try { await api.delete(`/clients/${clientId}/eway?${qs}`); load(); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-4" data-testid="eway-panel">
      <div className="flex flex-wrap items-center gap-2">
        {["outward", "inward"].map((d) => (
          <button key={d} data-testid={`eway-direction-${d}`} onClick={() => setDirection(d)}
            className={`rounded-md px-3 py-1.5 text-xs font-medium border ${direction === d ? "bg-indigo-600 text-white border-indigo-600" : "border-border text-muted-foreground hover:text-foreground"}`}>
            {d === "outward" ? "Outward (sales)" : "Inward (purchases)"}
          </button>
        ))}
        <span className="text-xs text-muted-foreground">Period {period}</span>
        <div className="ml-auto flex flex-wrap gap-2">
          <button data-testid="eway-template-button" onClick={() => download(`/clients/${clientId}/eway/template`, "EWay_Bill_Template.xlsx")} className={`${btn} border border-border hover:border-indigo-500/40`}><FileDown className="h-3.5 w-3.5" /> Template</button>
          {canEdit && (
            <label data-testid="eway-upload-label" className={`${btn} border border-border cursor-pointer hover:border-indigo-500/40 ${busy ? "pointer-events-none opacity-50" : ""}`}>
              <Upload className="h-3.5 w-3.5" /> Upload EWB export
              <input data-testid="eway-upload-input" type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ""; }} />
            </label>
          )}
          <button data-testid="eway-download-button" onClick={() => download(`/clients/${clientId}/eway/export?${qs}`, `${period}_EWay_${direction}.xlsx`)} className={`${btn} bg-indigo-600 hover:bg-indigo-700 text-white`}><Download className="h-3.5 w-3.5" /> Download Report</button>
          {canEdit && rep?.uploaded && <button data-testid="eway-clear-button" onClick={clear} className={`${btn} border border-rose-500/40 text-rose-400`}><Trash2 className="h-3.5 w-3.5" /> Clear</button>}
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        Upload the {direction === "outward" ? "\"generated by me\"" : "\"generated by others\""} e-way bill list exported from the e-way bill portal (or the GSTDesk template). Each bill is matched with the {direction === "outward" ? "sales" : "purchase"} register by document number.
        {rep?.uploaded && ` Last upload: ${rep.file_name} by ${rep.uploaded_by_name}.`}
      </p>
      {rep && <><Counts rep={rep} /><EwbTable rows={rep.rows} /><MissingTable rows={rep.missing_ewb} /></>}
    </div>
  );
}
